import React, { Component } from 'react';
import { array, bool, func, oneOf, object, shape, string } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import debounce from 'lodash/debounce';
import classNames from 'classnames';
import config from '../../config';
import { subUnitDivisors } from '../../currency-config';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { parse, stringify } from '../../util/urlHelpers';
import {
  derivePriceBounds,
  derivePriceDistribution,
  derivePriceRange,
  filterListingsByPriceRange,
  parsePriceRange,
} from '../../util/searchPriceRange';
import { publicListingMapCoordinates } from '../../util/maps';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import { SearchMap, ModalInMobile, Page } from '../../components';
import { TopbarContainer } from '../../containers';
import SearchFiltersModalV2 from '../../components/SearchFiltersModalV2/SearchFiltersModalV2';

import { searchMapListings, setActiveListing } from './SearchPage.duck';
import {
  pickSearchParamsOnly,
  validURLParamsForExtendedData,
  createSearchResultSchema,
} from './SearchPage.helpers';
import MainPanel from './MainPanel';
import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.
const MAP_RESULTS_PAGE_SIZE = 100;
const SEARCH_LISTING_IMAGE_LIMIT = 8;
const MOBILE_TOPBAR_HEIGHT = 60;
const MOBILE_SHEET_TOP_GAP = 12;
const MOBILE_SHEET_COLLAPSED_MIN = 300;
const MOBILE_SHEET_COLLAPSED_MAX = 380;
const MOBILE_SHEET_COLLAPSED_RATIO = 0.34;
const MOBILE_SHEET_MID_RATIO = 0.58;
const MOBILE_SHEET_MIN_STEP = 88;
const MOBILE_MAP_BADGE_OFFSET = 16;
const DATE_SEARCH_PARAM_NAMES = [
  'dates',
  'date_mode',
  'date_flex_days',
  'date_trip_length',
  'date_flex_month',
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const pickDateSearchParams = params =>
  DATE_SEARCH_PARAM_NAMES.reduce((acc, key) => {
    const value = params && params[key];

    return value !== null && typeof value !== 'undefined' && value !== ''
      ? { ...acc, [key]: value }
      : acc;
  }, {});

const isListingVisibleWithinBounds = (listing, bounds) => {
  const visiblePoint = publicListingMapCoordinates(listing);

  if (!visiblePoint || !bounds) {
    return false;
  }
  const north = Math.max(bounds.ne.lat, bounds.sw.lat);
  const south = Math.min(bounds.ne.lat, bounds.sw.lat);
  const east = Math.max(bounds.ne.lng, bounds.sw.lng);
  const west = Math.min(bounds.ne.lng, bounds.sw.lng);

  return (
    visiblePoint.lat >= south &&
    visiblePoint.lat <= north &&
    visiblePoint.lng >= west &&
    visiblePoint.lng <= east
  );
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : MODAL_BREAKPOINT,
      isSearchMapOpenOnMobile: props.tab === 'map',
      isMobileModalOpen: false,
      isDesktopFiltersModalOpen: false,
      mobileSheetSnap: 'collapsed',
      mobileSheetHeight: null,
      isDraggingMobileSheet: false,
    };
    this.mobileSheetDragState = null;
    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.onOpenDesktopFiltersModal = this.onOpenDesktopFiltersModal.bind(this);
    this.onCloseDesktopFiltersModal = this.onCloseDesktopFiltersModal.bind(this);
    this.handleApplyDesktopFilters = this.handleApplyDesktopFilters.bind(this);
    this.loadSearchMapListings = this.loadSearchMapListings.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleMobileSheetMouseDown = this.handleMobileSheetMouseDown.bind(this);
    this.handleMobileSheetMouseMove = this.handleMobileSheetMouseMove.bind(this);
    this.handleMobileSheetTouchStart = this.handleMobileSheetTouchStart.bind(this);
    this.handleMobileSheetTouchMove = this.handleMobileSheetTouchMove.bind(this);
    this.handleMobileSheetDragEnd = this.handleMobileSheetDragEnd.bind(this);
  }

  componentDidMount() {
    this.loadSearchMapListings();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleWindowResize);
      if (window.innerWidth < MODAL_BREAKPOINT) {
        this.syncMobileSheetHeight('collapsed');
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      this.loadSearchMapListings();
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleWindowResize);
    }
    this.removeMobileSheetDragListeners();
  }

  getPriceSourceListings() {
    const { listings, mapPriceListings } = this.props;
    return Array.isArray(mapPriceListings) && mapPriceListings.length > 0
      ? mapPriceListings
      : listings;
  }

  getResolvedFilterConfig(priceValue = null) {
    const { filterConfig } = this.props;
    const priceFilter = (filterConfig || []).find(filter => filter.id === 'price');

    if (!priceFilter || !priceFilter.config) {
      return filterConfig;
    }

    const selectedPrice = parsePriceRange(priceValue);
    const subUnitDivisor = subUnitDivisors[config.currency] || 100;
    const dynamicPriceConfig = derivePriceRange({
      listings: this.getPriceSourceListings(),
      selectedPrice,
      min: priceFilter.config.min,
      max: priceFilter.config.max,
      step: priceFilter.config.step,
      currency: config.currency,
      subUnitDivisor,
    });

    return filterConfig.map(filter => {
      if (filter.id !== 'price') {
        return filter;
      }

      return {
        ...filter,
        config: {
          ...filter.config,
          min: dynamicPriceConfig.minPrice,
          max: dynamicPriceConfig.maxPrice,
          step: dynamicPriceConfig.step,
        },
      };
    });
  }

  loadSearchMapListings() {
    const { location, onSearchMapListings } = this.props;
    const queryParams = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    const { page, ...rest } = queryParams;

    if (!rest.bounds) {
      return;
    }

    const originMaybe = config.sortSearchByDistance && rest.origin ? { origin: rest.origin } : {};

    onSearchMapListings({
      ...rest,
      ...originMaybe,
      page: 1,
      perPage: MAP_RESULTS_PAGE_SIZE,
      include: ['author', 'author.stripeAccount', 'images'],
      'fields.listing': ['title', 'geolocation', 'price', 'publicData'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'stripeAccount'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
      'limit.images': SEARCH_LISTING_IMAGE_LIMIT,
    }).catch(() => {});
  }

  getMobileLayoutHeight() {
    if (typeof window === 'undefined') {
      return 0;
    }

    return Math.max(window.innerHeight - MOBILE_TOPBAR_HEIGHT, 0);
  }

  getMobileSheetHeights() {
    const layoutHeight = this.getMobileLayoutHeight();
    const expanded = Math.max(
      layoutHeight - MOBILE_SHEET_TOP_GAP,
      MOBILE_SHEET_COLLAPSED_MIN + 140
    );
    const collapsedMax = Math.min(MOBILE_SHEET_COLLAPSED_MAX, expanded - MOBILE_SHEET_MIN_STEP * 2);
    const collapsed = clamp(
      Math.round(layoutHeight * MOBILE_SHEET_COLLAPSED_RATIO),
      MOBILE_SHEET_COLLAPSED_MIN,
      Math.max(MOBILE_SHEET_COLLAPSED_MIN, collapsedMax)
    );
    const mid = clamp(
      Math.round(layoutHeight * MOBILE_SHEET_MID_RATIO),
      collapsed + MOBILE_SHEET_MIN_STEP,
      expanded - MOBILE_SHEET_MIN_STEP
    );

    return {
      collapsed,
      mid,
      expanded,
    };
  }

  getMobileSheetHeightForSnap(snap) {
    const heights = this.getMobileSheetHeights();
    return heights[snap] || heights.collapsed;
  }

  syncMobileSheetHeight(nextSnap = this.state.mobileSheetSnap) {
    if (typeof window === 'undefined' || window.innerWidth >= MODAL_BREAKPOINT) {
      return;
    }

    const nextHeight = this.getMobileSheetHeightForSnap(nextSnap);

    this.setState(prevState => {
      if (
        prevState.mobileSheetSnap === nextSnap &&
        prevState.mobileSheetHeight === nextHeight &&
        !prevState.isDraggingMobileSheet
      ) {
        return null;
      }

      return {
        mobileSheetSnap: nextSnap,
        mobileSheetHeight: nextHeight,
        isDraggingMobileSheet: false,
      };
    });
  }

  handleWindowResize() {
    if (typeof window === 'undefined') {
      return;
    }

    const viewportWidth = window.innerWidth;
    const isMobileLayout = viewportWidth < MODAL_BREAKPOINT;

    this.setState(
      prevState => {
        const nextState = {};
        let hasChanges = false;

        if (prevState.viewportWidth !== viewportWidth) {
          nextState.viewportWidth = viewportWidth;
          hasChanges = true;
        }

        if (!isMobileLayout) {
          if (prevState.mobileSheetHeight !== null) {
            nextState.mobileSheetHeight = null;
            hasChanges = true;
          }

          if (prevState.isDraggingMobileSheet) {
            nextState.isDraggingMobileSheet = false;
            hasChanges = true;
          }
        }

        return hasChanges ? nextState : null;
      },
      () => {
        if (isMobileLayout && !this.state.isDraggingMobileSheet) {
          this.syncMobileSheetHeight(this.state.mobileSheetSnap);
        } else if (!isMobileLayout && this.mobileSheetDragState) {
          this.mobileSheetDragState = null;
          this.removeMobileSheetDragListeners();
        }
      }
    );
  }

  addMobileSheetDragListeners() {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('mousemove', this.handleMobileSheetMouseMove);
    window.addEventListener('mouseup', this.handleMobileSheetDragEnd);
    window.addEventListener('touchmove', this.handleMobileSheetTouchMove, { passive: false });
    window.addEventListener('touchend', this.handleMobileSheetDragEnd);
    window.addEventListener('touchcancel', this.handleMobileSheetDragEnd);
  }

  removeMobileSheetDragListeners() {
    if (typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('mousemove', this.handleMobileSheetMouseMove);
    window.removeEventListener('mouseup', this.handleMobileSheetDragEnd);
    window.removeEventListener('touchmove', this.handleMobileSheetTouchMove, false);
    window.removeEventListener('touchend', this.handleMobileSheetDragEnd);
    window.removeEventListener('touchcancel', this.handleMobileSheetDragEnd);
  }

  startMobileSheetDrag(clientY) {
    if (typeof window === 'undefined' || window.innerWidth >= MODAL_BREAKPOINT) {
      return;
    }

    const startHeight =
      this.state.mobileSheetHeight || this.getMobileSheetHeightForSnap(this.state.mobileSheetSnap);

    this.mobileSheetDragState = {
      startY: clientY,
      startHeight,
    };

    this.setState({
      mobileSheetHeight: startHeight,
      isDraggingMobileSheet: true,
    });

    this.addMobileSheetDragListeners();
  }

  updateMobileSheetDrag(clientY) {
    if (!this.mobileSheetDragState) {
      return;
    }

    const { collapsed, expanded } = this.getMobileSheetHeights();
    const delta = this.mobileSheetDragState.startY - clientY;
    const nextHeight = clamp(
      Math.round(this.mobileSheetDragState.startHeight + delta),
      collapsed,
      expanded
    );

    if (nextHeight !== this.state.mobileSheetHeight) {
      this.setState({ mobileSheetHeight: nextHeight });
    }
  }

  handleMobileSheetMouseDown(event) {
    event.preventDefault();
    this.startMobileSheetDrag(event.clientY);
  }

  handleMobileSheetMouseMove(event) {
    this.updateMobileSheetDrag(event.clientY);
  }

  handleMobileSheetTouchStart(event) {
    const touch = event.touches && event.touches[0];

    if (touch) {
      this.startMobileSheetDrag(touch.clientY);
    }
  }

  handleMobileSheetTouchMove(event) {
    if (!this.mobileSheetDragState) {
      return;
    }

    const touch = event.touches && event.touches[0];

    if (!touch) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    this.updateMobileSheetDrag(touch.clientY);
  }

  handleMobileSheetDragEnd() {
    if (!this.mobileSheetDragState) {
      return;
    }

    const currentHeight = this.state.mobileSheetHeight || this.mobileSheetDragState.startHeight;
    const heights = this.getMobileSheetHeights();
    const nextSnap = Object.keys(heights).reduce((closestSnap, snapKey) => {
      const closestDistance = Math.abs(heights[closestSnap] - currentHeight);
      const nextDistance = Math.abs(heights[snapKey] - currentHeight);
      return nextDistance < closestDistance ? snapKey : closestSnap;
    }, 'collapsed');

    this.mobileSheetDragState = null;
    this.removeMobileSheetDragListeners();

    this.setState({
      mobileSheetSnap: nextSnap,
      mobileSheetHeight: heights[nextSnap],
      isDraggingMobileSheet: false,
    });
  }

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;

    const routes = routeConfiguration();
    const searchPagePath = pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, sortConfig } = this.props;

      // parse query parameters, including a custom attribute named category
      const { address, bounds, mapSearch, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      const resolvedFilterConfig = this.getResolvedFilterConfig(rest.price);

      //const viewportMapCenter = SearchMap.getMapCenter(map);
      const originMaybe = config.sortSearchByDistance ? { origin: viewportCenter } : {};

      const preservedSearchParams = pickSearchParamsOnly(
        {
          ...rest,
          ...originMaybe,
          bounds: viewportBounds,
        },
        resolvedFilterConfig,
        sortConfig
      );

      const searchParams = {
        ...(address ? { address } : {}),
        ...preservedSearchParams,
        ...pickDateSearchParams(rest),
        mapSearch: true,
      };

      history.push(createResourceLocatorString('SearchPage', routes, {}, searchParams));
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  onOpenDesktopFiltersModal() {
    this.setState({ isDesktopFiltersModalOpen: true });
  }

  onCloseDesktopFiltersModal() {
    this.setState({ isDesktopFiltersModalOpen: false });
  }

  handleApplyDesktopFilters(searchParams) {
    const { history } = this.props;
    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  }

  render() {
    const {
      intl,
      listings,
      sortConfig,
      history,
      location,
      mapListings,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchMapListingsError,
      searchMapListingsInProgress,
      searchParams,
      activeListingId,
      onActivateListing,
    } = this.props;
    // eslint-disable-next-line no-unused-vars
    const { mapSearch, page, ...searchInURL } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const selectedPrice = parsePriceRange(searchInURL.price);
    const resolvedFilterConfig = this.getResolvedFilterConfig(searchInURL.price);
    const priceSourceListings = this.getPriceSourceListings();
    const resolvedPriceFilter = resolvedFilterConfig.find(filter => filter.id === 'price');
    const subUnitDivisor = subUnitDivisors[config.currency] || 100;
    const livePriceRange = derivePriceBounds({
      listings: priceSourceListings,
      min: resolvedPriceFilter?.config?.min ?? 0,
      max: resolvedPriceFilter?.config?.max ?? 1000,
      step: resolvedPriceFilter?.config?.step ?? 5,
      currency: config.currency,
      subUnitDivisor,
    });
    const dynamicPriceDistribution = derivePriceDistribution({
      listings: priceSourceListings,
      ...livePriceRange,
      currency: config.currency,
      subUnitDivisor,
    });
    const filteredListings = filterListingsByPriceRange({
      listings,
      selectedPrice,
      currency: config.currency,
      subUnitDivisor,
    });
    const filteredMapListings = filterListingsByPriceRange({
      listings: mapListings,
      selectedPrice,
      currency: config.currency,
      subUnitDivisor,
    });
    const hasExactDisplayPriceFilter = !!selectedPrice;

    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.sortSearchByDistance)
    const urlQueryParams = pickSearchParamsOnly(searchInURL, resolvedFilterConfig, sortConfig);

    // Page transition might initially use values from previous search
    const urlQueryString = stringify(urlQueryParams);
    const paramsQueryString = stringify(
      pickSearchParamsOnly(searchParams, resolvedFilterConfig, sortConfig)
    );
    const searchParamsAreInSync = urlQueryString === paramsQueryString;

    const validQueryParams = validURLParamsForExtendedData(searchInURL, resolvedFilterConfig);
    const desktopModalSearchParams = searchInURL.price
      ? { ...validQueryParams, price: searchInURL.price }
      : validQueryParams;
    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems = searchParamsAreInSync && hasPaginationInfo ? pagination.totalItems : 0;

    const isMobileLayout = this.state.viewportWidth < MODAL_BREAKPOINT;
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);
    const resolvedMobileSheetHeight = isMobileLayout
      ? this.state.mobileSheetHeight || this.getMobileSheetHeightForSnap(this.state.mobileSheetSnap)
      : null;
    const mobileSheetStyle =
      isMobileLayout && resolvedMobileSheetHeight
        ? { height: `${resolvedMobileSheetHeight}px` }
        : null;
    const mobileMapBadgeStyle =
      isMobileLayout && resolvedMobileSheetHeight
        ? { bottom: `${resolvedMobileSheetHeight + MOBILE_MAP_BADGE_OFFSET}px` }
        : null;
    const fitBoundsPadding = isMobileLayout
      ? {
          top: MOBILE_TOPBAR_HEIGHT + 20,
          right: 20,
          bottom: Math.max(
            (resolvedMobileSheetHeight ||
              this.getMobileSheetHeightForSnap(this.state.mobileSheetSnap)) +
              MOBILE_MAP_BADGE_OFFSET +
              24,
            220
          ),
          left: 20,
        }
      : {
          top: 28,
          right: 28,
          bottom: 28,
          left: 28,
        };

    const onMapIconClick = () => {
      this.useLocationSearchBounds = true;
      this.setState({ isSearchMapOpenOnMobile: true });
    };

    const { address, bounds, origin } = searchInURL || {};
    const visibleMapListings =
      mapSearch && bounds
        ? filteredMapListings.filter(listing => isListingVisibleWithinBounds(listing, bounds))
        : filteredMapListings;
    const useMapViewportResults = !!mapSearch;
    const panelListings = useMapViewportResults ? visibleMapListings : filteredListings;
    const panelResultsCount =
      useMapViewportResults || hasExactDisplayPriceFilter ? panelListings.length : totalItems;
    const panelPagination = useMapViewportResults || hasExactDisplayPriceFilter ? null : pagination;
    const panelSearchError = useMapViewportResults ? searchMapListingsError : searchListingsError;
    const panelSearchInProgress = useMapViewportResults
      ? searchMapListingsInProgress
      : searchInProgress;
    const panelListingsAreLoadedOverride = useMapViewportResults
      ? !searchMapListingsInProgress
      : hasExactDisplayPriceFilter
      ? !searchInProgress && searchParamsAreInSync
      : null;
    const mapLocationLabel =
      address || intl.formatMessage({ id: 'SearchResultsPanelV2.defaultLocation' });
    const { title, description, schema } = createSearchResultSchema(panelListings, address, intl);

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    const searchMapContent = (
      <div className={classNames(css.mapWrapper, { [css.mobileMapWrapper]: isMobileLayout })}>
        <SearchMap
          reusableContainerClassName={isMobileLayout ? css.mobileMap : css.map}
          activeListingId={activeListingId}
          bounds={bounds}
          center={origin}
          isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
          location={location}
          listings={visibleMapListings || []}
          onMapMoveEnd={this.onMapMoveEnd}
          onCloseAsModal={() => {
            onManageDisableScrolling('SearchPage.map', false);
          }}
          fitBoundsPadding={fitBoundsPadding}
          fitBoundsMinZoom={isMobileLayout ? 9 : 8.6}
          messages={intl.messages}
        />
        <div className={css.mapLocationBadge} style={mobileMapBadgeStyle}>
          {mapLocationLabel}
        </div>
      </div>
    );

    const mobileSearchPanel = (
      <div
        className={classNames(css.mobileSheetContainer, {
          [css.mobileSheetContainerDragging]: this.state.isDraggingMobileSheet,
        })}
        style={mobileSheetStyle}
      >
        <div
          className={css.mobileSheetGrabZone}
          onMouseDown={this.handleMobileSheetMouseDown}
          onTouchStart={this.handleMobileSheetTouchStart}
          role="presentation"
        >
          <span className={css.mobileSheetHandle} />
        </div>
        <MainPanel
          className={css.mobileSheetBody}
          urlQueryParams={validQueryParams}
          listings={panelListings}
          searchInProgress={panelSearchInProgress}
          searchListingsError={panelSearchError}
          searchParamsAreInSync={searchParamsAreInSync}
          onActivateListing={onActivateListing}
          onManageDisableScrolling={onManageDisableScrolling}
          onOpenModal={this.onOpenMobileModal}
          onCloseModal={this.onCloseMobileModal}
          onMapIconClick={onMapIconClick}
          pagination={panelPagination}
          resultsCount={panelResultsCount}
          listingsAreLoadedOverride={panelListingsAreLoadedOverride}
          searchParamsForPagination={parse(location.search)}
          showAsModalMaxWidth={MODAL_BREAKPOINT}
          history={history}
          intl={intl}
          onOpenDesktopFiltersModal={this.onOpenDesktopFiltersModal}
          filterConfig={resolvedFilterConfig}
          isMobileSearchPage
        />
      </div>
    );

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          className={topbarClasses}
          currentPage="SearchPage"
          currentSearchParams={validQueryParams}
          isDesktopFiltersModalOpen={this.state.isDesktopFiltersModalOpen}
          onOpenDesktopFiltersModal={this.onOpenDesktopFiltersModal}
        />
        <div className={css.container}>
          {isMobileLayout ? (
            <div className={css.mobileLayout}>
              <div className={css.mobileMapPanel}>{searchMapContent}</div>
              {mobileSearchPanel}
            </div>
          ) : (
            <>
              <MainPanel
                urlQueryParams={validQueryParams}
                listings={panelListings}
                searchInProgress={panelSearchInProgress}
                searchListingsError={panelSearchError}
                searchParamsAreInSync={searchParamsAreInSync}
                onActivateListing={onActivateListing}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                onMapIconClick={onMapIconClick}
                pagination={panelPagination}
                resultsCount={panelResultsCount}
                listingsAreLoadedOverride={panelListingsAreLoadedOverride}
                searchParamsForPagination={parse(location.search)}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                history={history}
                intl={intl}
                onOpenDesktopFiltersModal={this.onOpenDesktopFiltersModal}
                filterConfig={resolvedFilterConfig}
              />
              <ModalInMobile
                className={css.mapPanel}
                id="SearchPage.map"
                isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
              >
                {shouldShowSearchMap ? searchMapContent : null}
              </ModalInMobile>
            </>
          )}
        </div>
        <SearchFiltersModalV2
          isOpen={this.state.isDesktopFiltersModalOpen}
          onClose={this.onCloseDesktopFiltersModal}
          onApplySearchParams={this.handleApplyDesktopFilters}
          currentSearchParams={desktopModalSearchParams}
          filterConfig={resolvedFilterConfig}
          resultsCount={panelResultsCount}
          intl={intl}
          onManageDisableScrolling={onManageDisableScrolling}
          priceDistribution={dynamicPriceDistribution}
          priceRange={livePriceRange}
        />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  mapListings: [],
  mapPriceListings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  tab: 'listings',
  filterConfig: config.custom.filters,
  sortConfig: config.custom.sortConfig,
  activeListingId: null,
  searchMapListingsError: null,
  searchMapListingsInProgress: false,
};

SearchPageComponent.propTypes = {
  listings: array,
  mapListings: array,
  mapPriceListings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onSearchMapListings: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchMapListingsError: propTypes.error,
  searchMapListingsInProgress: bool,
  searchParams: object,
  tab: oneOf(['filters', 'listings', 'map']).isRequired,
  filterConfig: propTypes.filterConfig,
  sortConfig: propTypes.sortConfig,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    searchMapListingIds,
    searchMapListingsError,
    searchMapListingsInProgress,
    activeListingId,
  } = state.SearchPage;
  const pageListings = getListingsById(state, currentPageResultIds);
  const hasMapViewportResults = Array.isArray(searchMapListingIds);
  const mapViewportListings = hasMapViewportResults
    ? getListingsById(state, searchMapListingIds)
    : [];
  const mapListings = hasMapViewportResults ? mapViewportListings : pageListings;

  return {
    listings: pageListings,
    mapListings,
    mapPriceListings: mapViewportListings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchMapListingsError,
    searchMapListingsInProgress,
    searchParams,
    activeListingId,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onSearchMapListings: searchParams => dispatch(searchMapListings(searchParams)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(SearchPageComponent);

export default SearchPage;
